/**
 * Spelling and grammar checking through LanguageTool.
 *
 * Compared to plain dictionary checking, LanguageTool brings three things
 * that count for Elmar:
 *
 *   - Explanations instead of bare suggestions ("Nach einer lang gesprochenen
 *     Silbe schreibt man ß statt ss") — exactly the rule hints that the third
 *     help stage needs.
 *   - Sentence context: capitalization ("nach Hause"), compound writing
 *     ("rausgehen"), missing commas.
 *   - A much better hit rate for words written by ear.
 *
 * This layer must solve two things by itself:
 *
 *   1. LanguageTool delivers up to 20 suggestions, sorted by its internal
 *      score — for "fare" the word "fahre" comes only fifth, behind "wäre",
 *      "vage" and "Aare". Therefore they are re-sorted by similarity of
 *      sound; whoever writes by ear means the word that sounds the same.
 *   2. The service depends on the network. If it fails, writing must go on —
 *      the app then falls back to the local dictionary.
 */

import { Spellchecker, soundalike } from './dictionary.js'
import type { Finding } from '../types.js'

/**
 * The part of the LanguageTool answer that is actually read here.
 *
 * Everything the service sends beyond this stays untyped on purpose: what is
 * not read cannot break.
 */
interface LtMatch {
  offset: number
  length: number
  message?: string
  shortMessage?: string
  replacements?: { value: string }[]
  rule?: { id?: string; category?: { id?: string } }
}

interface LtResponse {
  matches?: LtMatch[]
}

/**
 * A finding as this layer writes it.
 *
 * It carries the fields of `Finding` except `suggestions`, which the app
 * reads here under the name the service uses (`replacements`), plus the two
 * that only the checking needs: the short message and where it came from.
 */
interface CheckFinding extends Omit<Finding, 'suggestions'> {
  shortMessage: string
  replacements: string[]
  source: 'lt' | 'local' | 'local-rule'
}

/** What `changedPart` gives back: the part, its place, and what stays. */
interface ChangedPart {
  text: string
  offset: number
  adopted: CheckFinding[]
}

/**
 * The own LanguageTool instance, through our own server.
 *
 * It used to be called directly, with the argument that a proxy would hide
 * the key but the key would be in the delivered code anyway. That argument
 * fell the moment there was a server for the photos: now the request takes
 * one hop through the Worker, which adds the key on its side. The browser
 * never sees it.
 *
 * A relative path, so it works the same in development (Vite proxies it) and
 * in production (same origin) — and so no CORS question arises at all.
 */
const DEFAULT_ENDPOINT = '/api/check'

/** Rule groups that would be only noise for a twelve-year-old. */
const IGNORED_RULES = new Set([
  'WHITESPACE_RULE',          // double spaces
  'UPPERCASE_SENTENCE_START', // fires far too early while typing
  'DE_CASE',                  // too many false alarms with names
  'EN_QUOTES',                // typographic quotation marks
  'DE_DOUBLE_PUNCTUATION',
  // COMMA_PARENTHESIS_WHITESPACE is NOT in here any more.
  //
  // It was, because it also fires on bracket spacing, which is noise. But it
  // is the rule for „Er kam , und ging" — a space on the wrong side of the
  // mark, which is one of the two things children get wrong about
  // punctuation and one of the easiest to learn. The other half (the missing
  // space after a full stop) was shown all along, so only half the lesson
  // ever arrived.
])

/**
 * Categories that we deliberately show. Everything else is discarded.
 *
 * The names must match what the server actually sends. It answers with
 * 'HILFESTELLUNG_KOMMASETZUNG', not with 'HILFESTELLUNG' — an exact
 * comparison therefore threw away every comma finding, and with it the most
 * frequent kind of mistake in a free text. Whoever writes without commas got
 * no mark at all.
 *
 * Therefore a prefix counts as a hit: 'HILFESTELLUNG' catches every
 * 'HILFESTELLUNG_*' that the server adds later as well.
 */
const SHOWN_CATEGORIES = [
  'TYPOS',          // spelling
  'GRAMMAR',        // grammar
  'CASING',         // capitalization
  'CONFUSED_WORDS', // das/dass, seit/seid
  'COMPOUNDING',    // compound writing
  'HILFESTELLUNG',  // rule hints, above all the comma ones
  'PUNCTUATION',    // comma before weil, wenn, wo
  'MISC',           // the missing space after a full stop
  // The space on the wrong side of the mark („Er kam , und"). Two locks held
  // this back, not one: the rule was in IGNORED_RULES and its category was
  // missing here. Only the whole category is let in, but of it only the
  // spacing findings survive — see `isSpaceFinding`; the rest of TYPOGRAPHY
  // is quotation marks and dashes, which nobody should be marked on.
  'TYPOGRAPHY',
]

/**
 * Is this a space that sits on the wrong side of a punctuation mark?
 *
 * The test is the shape, not the rule name: one character shorter, and what
 * goes is a space. „ ," → "," is one; a quotation mark swapped for a
 * typographic one is not.
 */
function isSpaceAroundMark(word: string, target: string | undefined): boolean {
  if (!target || target.length !== word.length - 1) return false
  let i = 0
  while (i < target.length && word[i] === target[i]) i++
  return word[i] === ' ' && word.slice(i + 1) === target.slice(i)
}

/** Does the category of a finding belong to the shown ones? */
function isShown(category: string | undefined): boolean {
  if (!category) return true
  return SHOWN_CATEGORIES.some(
    (c) => category === c || category.startsWith(c + '_')
  )
}

export class LanguageToolChecker {
  endpoint: string
  fallback: Spellchecker | null
  personal: Set<string>
  lastText: string | null
  lastResult: CheckFinding[]
  online: boolean

  /**
   * @param endpoint  URL of the LanguageTool API.
   * @param fallback  Local spellchecker for the case that the service cannot
   *                  be reached.
   */
  constructor(endpoint: string, fallback: Spellchecker | null = null) {
    this.endpoint = endpoint
    this.fallback = fallback
    this.personal = new Set()
    /** Last result, so that unchanged text is not checked again. */
    this.lastText = null
    this.lastResult = []
    this.online = true
  }

  /**
   * Determines which part of the text must be checked again.
   *
   * The comparison is against the text checked last: what is unchanged before
   * and after keeps its findings. Only the range in between is checked,
   * extended to sentence boundaries — cutting in the middle of a sentence
   * would deprive the grammar rules of their context.
   *
   * @returns {{text, offset, adopted}} The part, its position in the
   *   whole text, and the findings outside of it.
   */
  changedPart(text: string): ChangedPart {
    const old = this.lastText
    const whole: ChangedPart = { text, offset: 0, adopted: [] }
    if (!old || !this.lastResult) return whole

    // Common start and common end.
    let front = 0
    while (front < old.length && front < text.length && old[front] === text[front]) front++
    let back = 0
    while (
      back < old.length - front &&
      back < text.length - front &&
      old[old.length - 1 - back] === text[text.length - 1 - back]
    ) back++

    // Extend to sentence boundaries.
    let from = text.lastIndexOf('.', front)
    for (const z of ['!', '?', '\n']) from = Math.max(from, text.lastIndexOf(z, front))
    from = from < 0 ? 0 : from + 1

    const abIdx = text.length - back
    let to = text.length
    for (const z of ['.', '!', '?', '\n']) {
      const i = text.indexOf(z, abIdx)
      if (i >= 0) to = Math.min(to, i + 1)
    }

    // Worthwhile as soon as anything at all is saved. The base cost per
    // request is almost independent of the text; the saving comes almost
    // entirely from the sentence cache of the server — so it is better to
    // send a short part than the whole text.
    if (to - from >= text.length) return whole

    // Take over the findings outside the changed range, those behind it
    // shifted by the change in length.
    const shift = text.length - old.length
    const oldEnd = to - shift
    const adopted: CheckFinding[] = []
    for (const e of this.lastResult) {
      if (e.end <= from) adopted.push(e)
      else if (e.start >= oldEnd) {
        adopted.push({ ...e, start: e.start + shift, end: e.end + shift })
      }
    }

    return { text: text.slice(from, to), offset: from, adopted }
  }

  learn(word: string): string[] {
    this.personal.add(word)
    this.fallback?.learn(word)
    this.lastText = null
    return [...this.personal]
  }

  setPersonal(words: string[]): void {
    this.personal = new Set(words)
    for (const w of words) this.fallback?.learn(w)
  }

  /**
   * Checks the text and delivers the findings.
   *
   * @returns [{start, end, word, message, rule, replacements[], source}]
   */
  async check(
    text: string,
    { signal }: { signal?: AbortSignal } = {}
  ): Promise<CheckFinding[]> {
    if (!text.trim()) return []
    if (text === this.lastText) return this.lastResult

    // Check only the changed part.
    //
    // The server needs around 1.4 seconds per request — almost independent of
    // the text length, it is a fixed base cost. While typing this means:
    // every finished word costs 1.4 seconds, although only one sentence has
    // changed.
    //
    // Therefore the text is split into sentences and only the edited sentence
    // is sent. For all the others the findings of the last check apply,
    // shifted by the change in length. The result is the same, only much
    // faster.
    const part = this.changedPart(text)

    try {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ text: part.text, language: 'de-DE' }),
        signal,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as LtResponse

      let out: CheckFinding[] = [...part.adopted]
      for (const m of data.matches ?? []) {
        m.offset += part.offset
        const word = text.slice(m.offset, m.offset + m.length)
        if (this.personal.has(word)) continue
        if (IGNORED_RULES.has(m.rule?.id ?? '')) continue
        const cat = m.rule?.category?.id
        if (!isShown(cat)) continue
        // Of TYPOGRAPHY only the spacing: that category also holds quotation
        // marks and dashes, and nobody should be marked on those. A space on
        // the wrong side of a mark is plainly wrong; a straight quote is not.
        if (cat === 'TYPOGRAPHY' && !isSpaceAroundMark(word, m.replacements?.[0]?.value)) continue

        out.push({
          start: m.offset,
          end: m.offset + m.length,
          word,
          // The message from LanguageTool is often already a good hint —
          // but not always suitable for a child, so app.js only uses it when
          // no own rule applies.
          message: m.message ?? '',
          shortMessage: m.shortMessage ?? '',
          rule: m.rule?.id ?? '',
          category: cat ?? '',
          replacements: rank(word, (m.replacements ?? []).map((r) => r.value)),
          source: 'lt',
        })
      }

      // Run the own dass rule over the WHOLE text, not only over the checked
      // part: it is purely local and costs nothing, and this way the findings
      // stay complete even when an earlier sentence was not checked again.
      out = out.filter((e) => e.rule !== 'KRITZL_DASS')
      for (const extra of findMissingDass(text)) {
        const alreadyFound = out.some(
          (e) => extra.start < e.end && e.start < extra.end
        )
        if (!alreadyFound && !this.personal.has(extra.word)) out.push(extra)
      }
      out.sort((a, b) => a.start - b.start)

      this.online = true
      this.lastText = text
      this.lastResult = out
      return out
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err
      // Network gone or service down: writing must go on all the same.
      this.online = false
      if (!this.fallback) return []
      const out: CheckFinding[] = this.fallback.check(text).map((w) => ({
        start: w.start,
        end: w.end,
        word: w.word,
        message: '',
        shortMessage: '',
        rule: 'LOCAL_SPELLER',
        category: 'TYPOS',
        // Checked right above; the narrowing does not reach into the callback.
        replacements: this.fallback!.suggest(w.word, 5),
        source: 'local',
      }))
      this.lastText = text
      this.lastResult = out
      return out
    }
  }
}

/**
 * Own check for a missing "dass".
 *
 * LanguageTool detects the mistake only where it has a fixed rule
 * ("Ich weiß das ..." → KOMMA_DASS). After other frequent verbs — denke,
 * hoffe, glaube — it stays undetected. For Elmar this is one of the most
 * important cases of all, so it is added here.
 *
 * The rule is deliberately narrow: only when a "das" follows an introducing
 * verb and a personal pronoun or an article comes after it is it almost
 * certainly a subordinate clause — and thus "dass". If a verb stands there,
 * "das" is a relative pronoun and stays correct ("Das Auto, das dort
 * steht").
 */
const DASS_VERBS =
  /\b(denke|denkt|dachte|hoffe|hofft|hoffte|glaube|glaubt|glaubte|meine|meint|meinte|finde|findet|fand|wusste|wussten|weiß|weißt|sehe|sieht|sah|höre|hört|hörte|sage|sagt|sagte|erzählt|erzählte|verspreche|verspricht|bemerkt|merkt|merkte|vermute|vermutet|befürchte|befürchtet|freue|freut|freute|ärgert|ärgerte)\b/i

/** Words that introduce a subordinate clause after "dass". */
const CLAUSE_START =
  /^(ich|du|er|sie|es|wir|ihr|man|der|die|das|den|dem|ein|eine|einen|einem|einer|mein|dein|sein|unser|euer|kein|keine|alle|viele|manche|dieser|diese|dieses|jemand|niemand|nichts|etwas)\b/i

function findMissingDass(text: string): CheckFinding[] {
  const out: CheckFinding[] = []
  // Search for "das" as a word of its own, keeping the position.
  const re = /\bdas\b/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(text))) {
    const start = m.index
    // A match always has group 0; the index check cannot see that.
    const end = start + m[0]!.length

    // At the start of a sentence "das" is almost always right ("Das Auto
    // ist rot").
    const before = text.slice(Math.max(0, start - 60), start)
    if (!/\S/.test(before) || /[.!?]\s*$/.test(before)) continue

    // An introducing verb must stand before the "das" ...
    if (!DASS_VERBS.test(before)) continue

    // ... and the start of a subordinate clause behind it.
    const after = text.slice(end).replace(/^[\s,]+/, '')
    if (!CLAUSE_START.test(after)) continue

    // If a comma stands directly before it, LanguageTool usually has the
    // case already.
    out.push({
      start,
      end,
      word: text.slice(start, end),
      // The message is phrased as a question, like the own rule hints.
      message:
        'Kannst du „das" durch „dieses" oder „welches" ersetzen? Wenn nicht, ' +
        'schreibt man „dass" mit zwei s.',
      shortMessage: 'das oder dass?',
      rule: 'KRITZL_DASS',
      category: 'CONFUSED_WORDS',
      replacements: [m[0] === 'Das' ? 'Dass' : 'dass'],
      source: 'local-rule',
    })
  }
  return out
}

/**
 * Sorts suggestions by similarity of sound.
 *
 * LanguageTool orders by its own score; for "fare" the word "fahre" comes
 * only fifth. But whoever writes by ear means the word that sounds the same —
 * that one belongs at the front. Within the groups the original order is
 * kept.
 */
export function rank(word: string, suggestions: string[]): string[] {
  const key = soundalike(word)
  const lower = word.toLowerCase()
  return suggestions
    .map((s, i) => ({
      s,
      i,
      // The same sound counts most, then the same length (transposed
      // letters), then the order given by the service.
      score:
        (soundalike(s) === key ? 0 : 10) +
        // How far away, not merely how long.
        //
        // The length alone put every word of the same size in front: for
        // „is" that was „in" and „im" ahead of „ist", and for „Equater" it
        // was „Ecuador" ahead of „Äquator". A missing letter is one step
        // away; a different word of the same length is several.
        editDistance(word, s) +
        // A suggestion that differs ONLY in case goes to the back, not to the
        // front.
        //
        // For „is" the service offers „ist, IS, die, in, im". „IS" sounds the
        // same and has the same length, so it used to win outright — and the
        // card then pointed at the „i" as the mistake and offered a news
        // agency as the answer. Where capitalisation really is the mistake,
        // LanguageTool says so through its own rule and the word is spelled
        // differently anyway („erde" → „Erde" is a lower-case word made
        // upper-case, not an abbreviation in capitals).
        (s.toLowerCase() === lower ? (isShouty(s) ? 20 : -1) : 0),
    }))
    .sort((a, b) => a.score - b.score || a.i - b.i)
    .map((x) => x.s)
}

/**
 * How many single-character edits turn one word into the other.
 *
 * Plain Levenshtein. The words here are short — a handful of letters — so the
 * table costs nothing, and it says what „similar" means far better than a
 * comparison of lengths.
 */
function editDistance(a: string, b: string): number {
  const x = a.toLowerCase()
  const y = b.toLowerCase()
  let prev = Array.from({ length: y.length + 1 }, (_, j) => j)
  for (let i = 1; i <= x.length; i++) {
    const row = [i]
    for (let j = 1; j <= y.length; j++) {
      row[j] = Math.min(
        prev[j]! + 1,
        row[j - 1]! + 1,
        prev[j - 1]! + (x[i - 1] === y[j - 1] ? 0 : 1)
      )
    }
    prev = row
  }
  return prev[y.length]!
}

/**
 * All capitals, more than one letter: an abbreviation, not a spelling.
 *
 * „IS", „EU", „DNA" — what the service offers when a short word happens to
 * look like an acronym. A child writing „is" did not mean one.
 */
function isShouty(s: string): boolean {
  return s.length > 1 && s === s.toUpperCase() && s !== s.toLowerCase()
}

/**
 * Sets up the checking: LanguageTool with the local dictionary as fallback.
 *
 * The dictionary is always loaded, even when the service can be reached —
 * otherwise the app would stand without checking at the first network
 * failure, and that failure would come exactly when Elmar writes.
 */
export async function createChecker(
  endpoint: string = DEFAULT_ENDPOINT
): Promise<LanguageToolChecker> {
  let fallback: Spellchecker | null = null
  try {
    const [aff, dic] = await Promise.all([
      fetch('/dict/de_DE.aff').then((r) => r.arrayBuffer()),
      fetch('/dict/de_DE.dic').then((r) => r.arrayBuffer()),
    ])
    const dec = new TextDecoder('utf-8')
    fallback = new Spellchecker(dec.decode(aff), dec.decode(dic))
  } catch {
    // Without a fallback the app runs on, only without a substitute for the
    // network.
  }
  return new LanguageToolChecker(endpoint, fallback)
}
